import * as CURRENCIES from '@dinero.js/currencies';
import {
  dinero,
  Dinero,
  toFormat,
  toSnapshot,
  toUnit,
  Transformer,
  trimScale,
} from 'dinero.js';

import { CurrencyCode } from '@portals/types';

export const intlFormat = (
  dineroObject: Dinero<number>,
  locale: string = 'en-US'
) => {
  const transformer: Transformer<number> = ({ amount, currency }) =>
    amount.toLocaleString(locale, {
      style: 'currency',
      currency: currency.code,
      maximumFractionDigits: 2,
    });

  return toFormat(dineroObject, transformer);
};

export const formatCurrency = (value: number = 0, currency: string = 'USD') => {
  try {
    const dineroObject = dinero({
      amount: value,
      currency: CURRENCIES[currency],
    });

    return intlFormat(dineroObject);
  } catch (err) {
    console.error(
      `Failed to parse currency - value: ${value}, currency: ${currency}`
    );

    return 'Invalid amount';
  }
};

export function getCurrencySign(
  currencyCode: CurrencyCode,
  locale: string = 'en-US'
) {
  try {
    const numberFormat = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currencyCode,
    });
    const parts = numberFormat.formatToParts();

    const currencyPart = parts.find(
      (part) => part.type === 'currency'
    ) as Intl.NumberFormatPart;

    return currencyPart.value;
  } catch (e) {
    console.error(e);
    return '';
  }
}

export const convertFromMajorToMinor = (
  amount: number,
  currency: CurrencyCode
) => {
  if (amount === 0) return amount;

  const { exponent } = CURRENCIES[currency];

  const d = dinero({
    amount: amount * 10 ** exponent,
    currency: CURRENCIES[currency],
  });

  return toSnapshot(trimScale(d)).amount;
};

export const convertFromMinorToMajor = (
  amount: number,
  currency: CurrencyCode
) => {
  if (amount === 0) return amount;

  const d = dinero({
    amount,
    currency: CURRENCIES[currency],
  });

  return toUnit(d, { digits: 2, round: (value) => value });
};

export function formatNumber({
  value,
  locale = 'en-US',
}: {
  value: number | undefined;
  locale?: string;
}) {
  if (value === undefined) {
    return '--';
  }

  const formatter = new Intl.NumberFormat(locale, {
    minimumFractionDigits: 2,
  });

  return formatter.format(value);
}

interface FormatPriceParams {
  value: number | null | undefined;
  currencyCode: CurrencyCode;
  shouldConvertFromMinorToMajor?: boolean;
  withCurrencySign?: boolean;
  locale?: string;
}
export function formatPrice({
  value,
  currencyCode,
  shouldConvertFromMinorToMajor = true,
  withCurrencySign = true,
  locale = 'en-US',
}: FormatPriceParams) {
  const currencySign = getCurrencySign(currencyCode, locale);

  const emptyResult = withCurrencySign ? `${currencySign}--` : '--';

  if (value === undefined || value === null) {
    return emptyResult;
  }

  const formatter = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currencyCode,
  });

  let valueToFormat;

  if (shouldConvertFromMinorToMajor) {
    try {
      valueToFormat = convertFromMinorToMajor(value, currencyCode);
    } catch (e) {
      console.error(e);
      return emptyResult;
    }
  } else {
    valueToFormat = value;
  }

  const formattedValue = formatter.format(valueToFormat);

  if (withCurrencySign) {
    return formattedValue;
  } else {
    return formattedValue.replace(currencySign, '');
  }
}

export function abbreviateNumber(value: number) {
  return Intl.NumberFormat(undefined, {
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(value);
}
